import { db } from '@/firebase/config'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  onSnapshot
} from 'firebase/firestore'

/**
 * Create a new announcement
 * @param {Object} announcementData - Announcement data
 * @param {string} announcementData.message - Announcement message
 * @param {string} announcementData.scheduledDate - Scheduled date (YYYY-MM-DD)
 * @param {string} announcementData.scheduledTime - Scheduled time (HH:MM)
 * @param {string} announcementData.timezone - Timezone label (e.g., "EST")
 * @param {string} announcementData.priority - Priority level ("low", "medium", "high", "critical")
 * @param {string} announcementData.type - Announcement type ("maintenance", "general", "update")
 * @param {string} announcementData.expiresDate - Optional expiration date (YYYY-MM-DD)
 * @param {string} announcementData.expiresTime - Optional expiration time (HH:MM)
 * @param {string} announcementData.createdBy - Admin user ID
 * @returns {Promise<string>} - Announcement document ID
 */
export const createAnnouncement = async (announcementData) => {
  try {
    const announcementsRef = collection(db, 'announcements')

    const announcementDoc = {
      message: announcementData.message,
      scheduledDate: announcementData.scheduledDate,
      scheduledTime: announcementData.scheduledTime,
      timezone: announcementData.timezone || 'UTC',
      priority: announcementData.priority || 'medium',
      type: announcementData.type || 'general',
      isActive: announcementData.isActive !== undefined ? announcementData.isActive : true,
      createdBy: announcementData.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(announcementData.expiresDate && announcementData.expiresTime && {
        expiresDate: announcementData.expiresDate,
        expiresTime: announcementData.expiresTime
      })
    }

    const docRef = await addDoc(announcementsRef, announcementDoc)
    return docRef.id
  } catch (error) {
    console.error('Error creating announcement:', error)
    throw new Error('Failed to create announcement: ' + error.message)
  }
}

/**
 * Update an existing announcement
 * @param {string} announcementId - Announcement document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateAnnouncement = async (announcementId, updates) => {
  try {
    const announcementRef = doc(db, 'announcements', announcementId)

    const processedUpdates = { ...updates }
    processedUpdates.updatedAt = serverTimestamp()

    await updateDoc(announcementRef, processedUpdates)
  } catch (error) {
    console.error('Error updating announcement:', error)
    throw new Error('Failed to update announcement: ' + error.message)
  }
}

/**
 * Delete an announcement
 * @param {string} announcementId - Announcement document ID
 * @returns {Promise<void>}
 */
export const deleteAnnouncement = async (announcementId) => {
  try {
    const announcementRef = doc(db, 'announcements', announcementId)
    await deleteDoc(announcementRef)
  } catch (error) {
    console.error('Error deleting announcement:', error)
    throw new Error('Failed to delete announcement: ' + error.message)
  }
}

/**
 * Get all announcements (for admin)
 * @returns {Promise<Array>} - Array of announcement objects
 */
export const getAllAnnouncements = async () => {
  try {
    const announcementsRef = collection(db, 'announcements')
    const q = query(announcementsRef, orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)

    const announcements = []
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data()
      announcements.push({
        id: docSnapshot.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      })
    })

    return announcements
  } catch (error) {
    console.error('Error fetching announcements:', error)
    throw new Error('Failed to fetch announcements: ' + error.message)
  }
}

/**
 * Get active announcements (for display)
 * Filters based on:
 * - isActive flag
 * - scheduledTime (must be in past or now)
 * - expiresAt (must not be expired)
 * @returns {Promise<Array>} - Array of active announcement objects
 */
export const getActiveAnnouncements = async () => {
  try {
    const announcementsRef = collection(db, 'announcements')
    const q = query(
      announcementsRef,
      where('isActive', '==', true),
      orderBy('priority', 'desc'),
      orderBy('scheduledTime', 'desc')
    )

    const querySnapshot = await getDocs(q)
    const now = new Date()

    const activeAnnouncements = []
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data()
      
      // Check expiration if provided
      let isNotExpired = true
      if (data.expiresDate && data.expiresTime) {
        const expiresDateTime = new Date(`${data.expiresDate}T${data.expiresTime}`)
        isNotExpired = expiresDateTime > now
      } else if (data.expiresAt) {
        // Backward compatibility with old format
        const expiresAt = data.expiresAt?.toDate ? data.expiresAt.toDate() : data.expiresAt
        isNotExpired = expiresAt > now
      }

      if (isNotExpired) {
        activeAnnouncements.push({
          id: docSnapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        })
      }
    })

    return activeAnnouncements
  } catch (error) {
    console.error('Error fetching active announcements:', error)
    throw new Error('Failed to fetch active announcements: ' + error.message)
  }
}

/**
 * Get a single announcement by ID
 * @param {string} announcementId - Announcement document ID
 * @returns {Promise<Object>} - Announcement object
 */
export const getAnnouncementById = async (announcementId) => {
  try {
    const announcementRef = doc(db, 'announcements', announcementId)
    const docSnapshot = await getDoc(announcementRef)

    if (!docSnapshot.exists()) {
      throw new Error('Announcement not found')
    }

    const data = docSnapshot.data()
    return {
      id: docSnapshot.id,
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    }
  } catch (error) {
    console.error('Error fetching announcement:', error)
    throw new Error('Failed to fetch announcement: ' + error.message)
  }
}

/**
 * Subscribe to active announcements (real-time updates)
 * @param {Function} callback - Callback function that receives announcements array
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToActiveAnnouncements = (callback) => {
  try {
    const announcementsRef = collection(db, 'announcements')
    // Only filter by isActive - do all sorting client-side to avoid index issues
    const q = query(
      announcementsRef,
      where('isActive', '==', true)
    )

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const now = new Date()
        const activeAnnouncements = []

        querySnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data()
          
          // Check expiration if provided
          let isNotExpired = true
          if (data.expiresDate && data.expiresTime) {
            try {
              // Parse the date and time strings
              // scheduledDate format: "YYYY-MM-DD" (e.g., "2026-12-01")
              // scheduledTime format: "HH:MM" or "HH:MM AM/PM" (e.g., "05:00" or "05:00 AM")
              let timeStr = data.expiresTime
              // Convert 12-hour format to 24-hour if needed
              if (timeStr.includes('AM') || timeStr.includes('PM')) {
                const [timePart, period] = timeStr.split(' ')
                const [hours, minutes] = timePart.split(':')
                let hour24 = parseInt(hours, 10)
                if (period === 'PM' && hour24 !== 12) hour24 += 12
                if (period === 'AM' && hour24 === 12) hour24 = 0
                timeStr = `${hour24.toString().padStart(2, '0')}:${minutes}`
              }
              const expiresDateTime = new Date(`${data.expiresDate}T${timeStr}`)
              isNotExpired = expiresDateTime > now
            } catch (e) {
              // If parsing fails, assume not expired
              console.warn('Error parsing expiration date/time:', e)
              isNotExpired = true
            }
          }

          if (isNotExpired) {
            activeAnnouncements.push({
              id: docSnapshot.id,
              ...data,
              createdAt: data.createdAt?.toDate(),
              updatedAt: data.updatedAt?.toDate()
            })
          }
        })

        // Sort by priority and then by scheduled date/time (client-side)
        activeAnnouncements.sort((a, b) => {
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 }
          const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0)
          if (priorityDiff !== 0) return priorityDiff
          
          // Sort by scheduled date/time if available
          if (a.scheduledDate && b.scheduledDate) {
            try {
              let timeA = a.scheduledTime || ''
              let timeB = b.scheduledTime || ''
              
              // Convert 12-hour format to 24-hour if needed
              if (timeA.includes('AM') || timeA.includes('PM')) {
                const [timePart, period] = timeA.split(' ')
                const [hours, minutes] = timePart.split(':')
                let hour24 = parseInt(hours, 10)
                if (period === 'PM' && hour24 !== 12) hour24 += 12
                if (period === 'AM' && hour24 === 12) hour24 = 0
                timeA = `${hour24.toString().padStart(2, '0')}:${minutes}`
              }
              
              if (timeB.includes('AM') || timeB.includes('PM')) {
                const [timePart, period] = timeB.split(' ')
                const [hours, minutes] = timePart.split(':')
                let hour24 = parseInt(hours, 10)
                if (period === 'PM' && hour24 !== 12) hour24 += 12
                if (period === 'AM' && hour24 === 12) hour24 = 0
                timeB = `${hour24.toString().padStart(2, '0')}:${minutes}`
              }
              
              const dateA = new Date(`${a.scheduledDate}T${timeA}`)
              const dateB = new Date(`${b.scheduledDate}T${timeB}`)
              return dateB - dateA // Descending order (newest first)
            } catch (e) {
              return 0
            }
          }
          return 0
        })

        // console.log('Active announcements found:', activeAnnouncements.length)
        callback(activeAnnouncements)
      },
      (error) => {
        console.error('Error in announcement subscription:', error)
        callback([])
      }
    )

    return unsubscribe
  } catch (error) {
    console.error('Error setting up announcement subscription:', error)
    return () => {}
  }
}
